import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const CustomViewSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  module: z.string(),
  section: z.string(),
  config: z.record(z.string(), z.unknown()),
  displayOrder: z.int().nonnegative(),
  title: z.string(),
  creatorName: z.string().nullable(),
  creatorId: UUIDSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const CreateCustomViewInputSchema = z.object({
  module: z.string(),
  section: z.string(),
  config: z.record(z.string(), z.unknown()),
  title: z.string(),
  displayOrder: z.int().nonnegative().optional(),
});

export const ListCustomViewsInputSchema = z.object({
  module: z.string(),
  section: z.string(),
});

export const ListCustomViewsResponseSchema = z.object({
  views: z.array(CustomViewSchema),
});

export const UpdateCustomViewInputSchema = z.object({
  id: UUIDSchema,
  data: z
    .object({
      config: z.record(z.string(), z.unknown()).optional(),
      title: z.string().optional(),
      displayOrder: z.int().nonnegative().optional(),
    })
    .refine(
      (data) => Object.keys(data).length > 0,
      "At least one field must be provided",
    ),
});

export const DeleteCustomViewInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteCustomViewResponseSchema = z.object({
  success: z.boolean(),
  module: z.string().nullable(),
  section: z.string().nullable(),
});

export const ReorderCustomViewsInputSchema = z.object({
  module: z.string(),
  section: z.string(),
  orderedIds: z.array(UUIDSchema),
});

export const ReorderCustomViewsResponseSchema = z.object({
  success: z.boolean(),
});

export const customViewsContracts = {
  create: oc
    .input(CreateCustomViewInputSchema)
    .output(CustomViewSchema)
    .route({ method: "POST" }),
  list: oc
    .input(ListCustomViewsInputSchema)
    .output(ListCustomViewsResponseSchema)
    .route({ method: "GET" }),
  update: oc
    .input(UpdateCustomViewInputSchema)
    .output(CustomViewSchema.nullable())
    .route({ method: "PUT" }),
  delete: oc
    .input(DeleteCustomViewInputSchema)
    .output(DeleteCustomViewResponseSchema)
    .route({ method: "DELETE" }),
  reorder: oc
    .input(ReorderCustomViewsInputSchema)
    .output(ReorderCustomViewsResponseSchema)
    .route({ method: "POST" }),
} as const;

export type CustomView = z.infer<typeof CustomViewSchema>;
export type CreateCustomViewInput = z.infer<typeof CreateCustomViewInputSchema>;
export type ListCustomViewsInput = z.infer<typeof ListCustomViewsInputSchema>;
export type ListCustomViewsResponse = z.infer<
  typeof ListCustomViewsResponseSchema
>;
export type UpdateCustomViewInput = z.infer<typeof UpdateCustomViewInputSchema>;
export type DeleteCustomViewInput = z.infer<typeof DeleteCustomViewInputSchema>;
export type DeleteCustomViewResponse = z.infer<
  typeof DeleteCustomViewResponseSchema
>;
export type ReorderCustomViewsInput = z.infer<
  typeof ReorderCustomViewsInputSchema
>;
export type ReorderCustomViewsResponse = z.infer<
  typeof ReorderCustomViewsResponseSchema
>;
